import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaUser, FaPlus, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import { usePageTitle } from '../hooks/usePageTitle';
import { systemUserService } from '../services/systemUserService';
import { roleService, Role } from '../services/roleService';
import { projectService, Project } from '../services/projectService';
import '../css/pages/AddSystemUser.css';

interface ProjectRoleAssignment {
  projectId: number;
  roleIds: number[];
}

const AddSystemUser: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const pathname = window.location.pathname;
  const isEditMode = pathname.includes('/edit');
  const isViewMode = pathname.includes('/view');
  const isCreateMode = !isEditMode && !isViewMode;
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    employeeName: ''
  });
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [projectRoleAssignments, setProjectRoleAssignments] = useState<ProjectRoleAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id && projects.length > 0 && roles.length > 0) {
      loadUserData(parseInt(id));
    }
  }, [isEditMode, isViewMode, id, projects, roles]);

  const loadUserData = async (userId: number) => {
    try {
      setLoading(true);
      const userData = await systemUserService.getSystemUserById(userId);
      
      setFormData({
        username: userData.username,
        password: '', // Don't load password for security
        employeeName: userData.employeeName
      });

      // Load user's project role assignments
      if (userData.userRoles && userData.userRoles.length > 0) {
        const assignments: ProjectRoleAssignment[] = [];
        userData.userRoles.forEach((userRole: any) => {
          const existingAssignment = assignments.find(a => a.projectId === userRole.project.id);
          if (existingAssignment) {
            existingAssignment.roleIds.push(userRole.role.id);
          } else {
            assignments.push({
              projectId: userRole.project.id,
              roleIds: [userRole.role.id]
            });
          }
        });
        setProjectRoleAssignments(assignments);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [projectsData, rolesResponse] = await Promise.all([
        projectService.getProjects(true), // Get only active projects
        roleService.getRoles()
      ]);
      
      setProjects(projectsData);
      setRoles(rolesResponse.roles);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addProjectAssignment = () => {
    if (projects.length > 0) {
      setProjectRoleAssignments(prev => [...prev, {
        projectId: projects[0].id,
        roleIds: []
      }]);
    }
  };

  const removeProjectAssignment = (index: number) => {
    setProjectRoleAssignments(prev => prev.filter((_, i) => i !== index));
  };

  const updateProjectAssignment = (index: number, projectId: number) => {
    setProjectRoleAssignments(prev => prev.map((assignment, i) => 
      i === index ? { ...assignment, projectId, roleIds: [] } : assignment
    ));
  };

  const updateRoleAssignment = (index: number, roleId: number, checked: boolean) => {
    setProjectRoleAssignments(prev => prev.map((assignment, i) => {
      if (i === index) {
        const roleIds = checked
          ? [...assignment.roleIds, roleId]
          : assignment.roleIds.filter(id => id !== roleId);
        return { ...assignment, roleIds };
      }
      return assignment;
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.employeeName.trim()) {
      newErrors.employeeName = 'Employee name is required';
    }

    if (projectRoleAssignments.length === 0) {
      newErrors.projects = 'At least one project assignment is required';
    }

    // Check if all project assignments have at least one role
    const invalidAssignments = projectRoleAssignments.some(assignment => assignment.roleIds.length === 0);
    if (invalidAssignments) {
      newErrors.roles = 'Each project must have at least one role assigned';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && id) {
        // Update existing user
        const updateData = {
          username: formData.username,
          password: formData.password || 'unchanged', // Use placeholder if no new password
          employeeName: formData.employeeName,
          projectRoles: projectRoleAssignments
        };

        const result = await systemUserService.updateSystemUser(parseInt(id), updateData);
      } else {
        // Create new user
        const result = await systemUserService.createSystemUser({
          username: formData.username,
          password: formData.password,
          employeeName: formData.employeeName,
          projectRoles: projectRoleAssignments
        });
      }
      
      // Add a small delay to ensure the API call completes fully
      setTimeout(() => {
        navigate('/auth/user');
      }, 500);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
      setErrors({ submit: `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.` });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/auth/user');
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}>
            <FaUser className="page-icon" style={{ marginRight: '8px' }} /> 
            {isViewMode ? 'View System User' : isEditMode ? 'Edit System User' : 'Add System User'}
          </h1>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-secondary" onClick={handleCancel}>
            <FaTimes />
            Cancel
          </button>
          {!isViewMode && (
            <button 
              type="submit" 
              form="add-user-form"
              className="btn-primary"
              disabled={loading}
            >
              <FaSave />
              {loading 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update User' : 'Create User')
              }
            </button>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="add-system-user-content">
        <form id="add-user-form" onSubmit={handleSubmit} className="user-form">
          {/* Basic Information */}
          <div className="form-section">
            <h2>Basic Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="username">Username *</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={errors.username ? 'error' : ''}
                  placeholder="Enter username"
                  disabled={isViewMode}
                />
                {errors.username && <span className="error-message">{errors.username}</span>}
              </div>

              <div className="form-field">
                <label htmlFor="password">Password *</label>
                <input
                  id="password"
                  name="password"
                  type="text"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'error' : ''}
                  placeholder="Enter password (plain text)"
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              <div className="form-field full-width">
                <label htmlFor="employeeName">Employee Name *</label>
                <input
                  id="employeeName"
                  name="employeeName"
                  type="text"
                  value={formData.employeeName}
                  onChange={handleInputChange}
                  className={errors.employeeName ? 'error' : ''}
                  placeholder="Enter employee full name"
                />
                {errors.employeeName && <span className="error-message">{errors.employeeName}</span>}
              </div>
            </div>
          </div>

          {/* Project Role Assignments */}
          <div className="form-section">
            <div className="section-header">
              <h2>Project Role Assignments</h2>
              <button 
                type="button" 
                className="btn-secondary btn-small"
                onClick={addProjectAssignment}
              >
                <FaPlus />
                Add Project
              </button>
            </div>

            {errors.projects && <div className="error-message section-error">{errors.projects}</div>}
            {errors.roles && <div className="error-message section-error">{errors.roles}</div>}

            {projectRoleAssignments.length === 0 ? (
              <div className="empty-assignments">
                <p>No project assignments added yet. Click "Add Project" to assign roles.</p>
              </div>
            ) : (
              <div className="project-assignments">
                {projectRoleAssignments.map((assignment, index) => (
                  <div key={index} className="project-assignment-card">
                    <div className="assignment-header">
                      <div className="project-selector">
                        <label>Project:</label>
                        <select
                          value={assignment.projectId}
                          onChange={(e) => updateProjectAssignment(index, parseInt(e.target.value))}
                        >
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn-danger btn-small"
                        onClick={() => removeProjectAssignment(index)}
                      >
                        <FaTrash />
                        Remove
                      </button>
                    </div>

                    <div className="roles-section">
                      <label>Roles for this project:</label>
                      <div className="roles-grid">
                        {roles.map(role => (
                          <label key={role.id} className="role-checkbox">
                            <input
                              type="checkbox"
                              checked={assignment.roleIds.includes(role.id)}
                              onChange={(e) => updateRoleAssignment(index, role.id, e.target.checked)}
                            />
                            <span className="role-name">{role.name}</span>
                            <span className="role-description">{role.description}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddSystemUser;
